import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";

const STATS_COLLECTION = "stats";
const LEGACY_STATS_COLLECTION = "monthly_stats";

const BACKUP_COLLECTIONS = [
  "income",
  "expenses",
  "payments",
  "materials",
  "workers",
  "invoice_clients",
  "invoices",
  "projects",
  "offers",
  "calendar_events",
  "timesheets",
  "stats",
  "calculations",
  "project_documents",
  "reports",
];

function normalizeFirestoreValue(value) {
  if (value && typeof value === "object") {
    if (typeof value.toDate === "function") {
      return value.toDate().toISOString();
    }

    if (Array.isArray(value)) {
      return value.map(normalizeFirestoreValue);
    }

    const result = {};
    for (const [key, nested] of Object.entries(value)) {
      result[key] = normalizeFirestoreValue(nested);
    }
    return result;
  }

  return value;
}

async function readCollection(name) {
  const snapshot = await getDocs(collection(db, name));
  return snapshot.docs.map((item) => ({
    id: item.id,
    ...normalizeFirestoreValue(item.data()),
  }));
}

function getPayloadEntries(payload, collectionName) {
  const data = payload?.data || {};

  if (collectionName === STATS_COLLECTION) {
    if (Array.isArray(data[STATS_COLLECTION])) return data[STATS_COLLECTION];
    if (Array.isArray(data[LEGACY_STATS_COLLECTION])) return data[LEGACY_STATS_COLLECTION];
    return [];
  }

  return Array.isArray(data[collectionName]) ? data[collectionName] : [];
}

export function summarizePayload(payload) {
  const breakdown = {
    income: getPayloadEntries(payload, "income").length,
    expenses: getPayloadEntries(payload, "expenses").length,
    payments: getPayloadEntries(payload, "payments").length,
    materials: getPayloadEntries(payload, "materials").length,
    workers: getPayloadEntries(payload, "workers").length,
    invoice_clients: getPayloadEntries(payload, "invoice_clients").length,
    invoices: getPayloadEntries(payload, "invoices").length,
    projects: getPayloadEntries(payload, "projects").length,
    offers: getPayloadEntries(payload, "offers").length,
    calendar_events: getPayloadEntries(payload, "calendar_events").length,
    timesheets: getPayloadEntries(payload, "timesheets").length,
    stats: getPayloadEntries(payload, STATS_COLLECTION).length,
    calculations: getPayloadEntries(payload, "calculations").length,
    project_documents: getPayloadEntries(payload, "project_documents").length,
    reports: getPayloadEntries(payload, "reports").length,
    company_settings: payload?.data?.settings?.company ? 1 : 0,
  };

  const entries = Object.values(breakdown).reduce((sum, value) => sum + Number(value || 0), 0);

  return { breakdown, entries };
}

export async function createBackupPayload() {
  const data = {};

  for (const collectionName of BACKUP_COLLECTIONS) {
    data[collectionName] = await readCollection(collectionName);
  }

  const settingsRef = doc(db, "settings", "company");
  const settingsSnap = await getDoc(settingsRef);

  return {
    meta: {
      app: "BauHeld",
      version: 2,
      exportedAt: new Date().toISOString(),
      collections: BACKUP_COLLECTIONS,
      mode: "local-pc-backup",
    },
    data: {
      ...data,
      settings: {
        company: settingsSnap.exists() ? normalizeFirestoreValue(settingsSnap.data()) : null,
      },
    },
    summary: summarizePayload({
      data: {
        ...data,
        settings: {
          company: settingsSnap.exists() ? normalizeFirestoreValue(settingsSnap.data()) : null,
        },
      },
    }),
  };
}

export async function parseBackupFile(file) {
  const text = await file.text();
  const payload = JSON.parse(text);

  if (!payload?.data || typeof payload.data !== "object") {
    throw new Error("Invalid backup file. Missing data section.");
  }

  return {
    payload,
    summary: summarizePayload(payload),
  };
}

async function clearCollection(name) {
  const snapshot = await getDocs(collection(db, name));
  const docs = snapshot.docs;

  for (let i = 0; i < docs.length; i += 400) {
    const chunk = docs.slice(i, i + 400);
    const batch = writeBatch(db);
    chunk.forEach((entry) => batch.delete(entry.ref));
    await batch.commit();
  }
}

async function rebuildStatsIfAvailable() {
  try {
    const module = await import("../utils/rebuildMonthlyStats");
    if (typeof module.rebuildMonthlyStats === "function") {
      await module.rebuildMonthlyStats();
    } else if (typeof module.default === "function") {
      await module.default();
    }
  } catch (error) {
    console.warn("rebuildMonthlyStats not executed:", error);
  }
}

function restoreDocPayload(entry) {
  if (!entry || typeof entry !== "object") return {};

  if (entry.data && typeof entry.data === "object" && !Array.isArray(entry.data)) {
    return entry.data;
  }

  const { id, ...rest } = entry;
  return rest;
}

async function writeCollectionDocs(name, docs) {
  for (let i = 0; i < docs.length; i += 400) {
    const chunk = docs.slice(i, i + 400);
    const batch = writeBatch(db);

    chunk.forEach((entry) => {
      const id = entry?.id;
      if (!id) return;

      const payload = restoreDocPayload(entry);
      batch.set(doc(db, name, id), payload, { merge: false });
    });

    await batch.commit();
  }
}

export async function restoreBackupReplace(payload) {
  for (const collectionName of BACKUP_COLLECTIONS) {
    await clearCollection(collectionName);
  }

  for (const collectionName of BACKUP_COLLECTIONS) {
    const docs = getPayloadEntries(payload, collectionName);
    await writeCollectionDocs(collectionName, docs);
  }

  const companySettings = payload?.data?.settings?.company;
  if (companySettings) {
    await setDoc(doc(db, "settings", "company"), companySettings, { merge: false });
  } else {
    try {
      await deleteDoc(doc(db, "settings", "company"));
    } catch (error) {
      console.warn("Company settings delete skipped:", error);
    }
  }

  await rebuildStatsIfAvailable();

  return {
    mode: "replace",
    summary: summarizePayload(payload),
  };
}

export async function importBackupMerge(payload) {
  for (const collectionName of BACKUP_COLLECTIONS) {
    const docs = getPayloadEntries(payload, collectionName);
    await writeCollectionDocs(collectionName, docs);
  }

  const companySettings = payload?.data?.settings?.company;
  if (companySettings) {
    await setDoc(doc(db, "settings", "company"), companySettings, { merge: true });
  }

  await rebuildStatsIfAvailable();

  return {
    mode: "merge",
    summary: summarizePayload(payload),
  };
}

export async function resetSelectedCollections(selected = []) {
  if (!Array.isArray(selected) || !selected.length) {
    throw new Error("No collections selected for reset.");
  }

  const collectionNames = [...BACKUP_COLLECTIONS, LEGACY_STATS_COLLECTION];

  for (const name of selected) {
    if (name === "settings/company") {
      await deleteDoc(doc(db, "settings", "company"));
      continue;
    }

    if (!collectionNames.includes(name)) continue;

    await clearCollection(name === LEGACY_STATS_COLLECTION ? STATS_COLLECTION : name);
  }

  if (
    selected.includes(STATS_COLLECTION) ||
    selected.includes(LEGACY_STATS_COLLECTION) ||
    selected.some((name) => BACKUP_COLLECTIONS.includes(name) && name !== STATS_COLLECTION)
  ) {
    await rebuildStatsIfAvailable();
  }

  return true;
}
